import { Component, DestroyRef, effect, inject, signal, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription, firstValueFrom, forkJoin, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { PrimengImports } from '../../../../../shared/primeng/primeng.imports';
import {
  AddressSuggestion,
  GeocodingService,
  ZipLookupResult,
} from '../../../../../core/services/geocoding.service';
import type { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { LeadService } from '../../../../../core/services/lead.service';
import { FeasibilityQuestionOption } from '../../../../../core/models/lead.model';
import {
  fieldErrorMessage,
  PROPERTY_TYPE_OPTIONS,
  resolveCountyOption,
  resolveStateOption,
  US_COUNTY_OPTIONS,
  US_STATE_OPTIONS,
} from '../../models/property-intake.model';

@Component({
  selector: 'krc-contact-property-step',
  standalone: true,
  imports: [ReactiveFormsModule, PrimengImports],
  templateUrl: './contact-property-step.component.html',
  styleUrl: './contact-property-step.component.scss',
})
export class ContactPropertyStepComponent {
  private readonly geocodingService = inject(GeocodingService);
  private readonly leadService = inject(LeadService);
  private readonly destroyRef = inject(DestroyRef);
  private zipSubscription?: Subscription;
  private zipRawSubscription?: Subscription;
  private zipQuerySubscription?: Subscription;
  private streetAddressSubscription?: Subscription;
  private lastLookedUpZip = '';

  readonly form = input.required<FormGroup>();

  /** Fetched from the question bank (INTAKE_CONTACT_METHOD) instead of a hardcoded
   *  list — falls back to this default set only if that fetch fails. Signals, not
   *  plain properties — a plain-property reassignment landing after this async
   *  fetch resolves isn't reliably picked up by the template (same issue documented
   *  in IntendedUseStepComponent); a signal guarantees it's actually seen. */
  readonly preferredContactOptions = signal<FeasibilityQuestionOption[]>([
    { label: 'Email', value: 'email' },
    { label: 'Phone', value: 'phone' },
  ]);

  readonly buildTimelineOptions = signal<FeasibilityQuestionOption[]>([]);
  readonly stateOptions = US_STATE_OPTIONS;
  readonly countyOptions = US_COUNTY_OPTIONS;
  readonly propertyTypeOptions = PROPERTY_TYPE_OPTIONS;
  readonly isLookingUpZip = signal(false);
  readonly zipLookupError = signal<string | null>(null);
  readonly isLocating = signal(false);
  readonly locationError = signal<string | null>(null);
  readonly zipMismatchError = signal<string | null>(null);
  zipQuery: string | AddressSuggestion = '';
  readonly zipSuggestions = signal<AddressSuggestion[]>([]);
  readonly isSearchingZip = signal(false);
  addressQuery: string | AddressSuggestion = '';
  readonly addressSuggestions = signal<AddressSuggestion[]>([]);
  readonly isSearchingAddress = signal(false);
  readonly isResolvingAddress = signal(false);
  readonly addressLookupError = signal<string | null>(null);
  private readonly resolvedBounds = signal<google.maps.LatLngBoundsLiteral | null>(null);

  constructor() {
    firstValueFrom(this.leadService.getFeasibilityQuestions('INTAKE', 'CONTACT'))
      .then(questions => {
        const contactMethod = questions.find(x => x.questionCode === 'INTAKE_CONTACT_METHOD');
        const contactOptions = contactMethod?.options?.filter(option => option.value !== 'either');
        if (contactOptions?.length) this.preferredContactOptions.set(contactOptions);

        const buildTimeline = questions.find(x => x.questionCode === 'INTAKE_BUILD_TIMELINE');
        if (buildTimeline?.options?.length) this.buildTimelineOptions.set(buildTimeline.options);
      })
      .catch(() => { /* keep the built-in default sets above */ });

    effect(() => {
      const form = this.form();
      const zipControl = form.get('zipCode');

      this.zipSubscription?.unsubscribe();
      this.zipSubscription = zipControl?.valueChanges
        .pipe(
          map(value => this.sanitizeZip(value)),
          debounceTime(400),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(value => this.triggerZipLookup(value));
      this.zipQuery = zipControl?.value ?? '';
      this.zipQuerySubscription?.unsubscribe();
      this.zipQuerySubscription = zipControl?.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(value => { this.zipQuery = value ?? ''; });
      this.zipRawSubscription?.unsubscribe();
      this.zipRawSubscription = zipControl?.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(value => {
          const sanitized = this.sanitizeZip(value);
          if (sanitized !== value) {
            zipControl.setValue(sanitized, { emitEvent: false });
          }
          if (!/^\d{5}$/.test(sanitized)) {
            this.clearResolvedLocationFields();
          }
        });
      const streetControl = form.get('streetAddress');
      this.addressQuery = streetControl?.value ?? '';
      this.streetAddressSubscription?.unsubscribe();
      this.streetAddressSubscription = streetControl?.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(value => { this.addressQuery = value ?? ''; });
    });
  }

  // Catches ZIP codes typed in full without tabbing away slowly enough for the
  // debounced valueChanges above to have already fired.
  onZipBlur(): void {
    this.triggerZipLookup(this.form().get('zipCode')?.value);
  }

  private sanitizeZip(value: string | null | undefined): string {
    return (value ?? '').replace(/\D/g, '').slice(0, 5);
  }

  // Merges plain postal-code matches (e.g. "92154, San Diego, CA, USA") with real
  // street addresses so the ZIP dropdown can show street + ZIP together. Google's
  // Autocomplete treats a numeric query as a house-number search, not a ZIP
  // prefix, so a raw merge leaks unrelated matches (typing "921" once returned
  // "921 Howard Street, San Francisco" and even a Las Vegas result) — each
  // address candidate's real ZIP is fetched and checked against the typed digits
  // before it's allowed into the list, so anything that isn't actually in that
  // ZIP is dropped rather than shown.
  searchZip(event: AutoCompleteCompleteEvent): void {
    this.isSearchingZip.set(true);
    const typedDigits = this.sanitizeZip(event.query);
    forkJoin([
      this.geocodingService.searchAddresses(event.query, undefined, ['(regions)']),
      this.geocodingService.searchAddresses(event.query, undefined, ['address']).pipe(
        switchMap(suggestions => (typedDigits ? this.geocodingService.attachZipCodes(suggestions) : of([]))),
        map(suggestions => suggestions.filter(s => !!s.zipCode && s.zipCode.startsWith(typedDigits))),
      ),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([regionSuggestions, addressSuggestions]) => {
        this.isSearchingZip.set(false);
        this.zipSuggestions.set([...regionSuggestions, ...addressSuggestions]);
      });
  }

  onZipQueryChange(value: string | AddressSuggestion): void {
    if (typeof value === 'string') {
      this.form().get('zipCode')?.setValue(this.sanitizeZip(value));
    }
  }

  onZipRawInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const sanitized = this.sanitizeZip(inputEl.value);
    if (sanitized === inputEl.value) {
      return;
    }
    inputEl.value = sanitized;
    this.zipQuery = sanitized;
    this.form().get('zipCode')?.setValue(sanitized);
  }

  searchAddress(event: AutoCompleteCompleteEvent): void {
    this.isSearchingAddress.set(true);
    this.geocodingService.searchAddresses(event.query, this.resolvedBounds())
      .pipe(
        switchMap(suggestions => this.geocodingService.attachZipCodes(suggestions)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(suggestions => {
        this.isSearchingAddress.set(false);
        this.addressSuggestions.set(suggestions);
      });
  }

  isStreetAddressEnabled(): boolean {
    return /^\d{5}$/.test(this.sanitizeZip(this.form().get('zipCode')?.value));
  }

  private addressBlurRevertHandle?: ReturnType<typeof setTimeout>;

  onAddressBlur(): void {
    clearTimeout(this.addressBlurRevertHandle);
    this.addressBlurRevertHandle = setTimeout(() => {
      const confirmed = this.form().get('streetAddress')?.value ?? '';
      this.addressQuery = confirmed;
      this.addressSuggestions.set([]);
      this.form().get('streetAddress')?.markAsTouched();
      const inputEl = document.getElementById('streetAddress') as HTMLInputElement | null;
      if (inputEl) {
        inputEl.value = confirmed;
      }
    }, 250);
  }

  onAddressSelect(event: AutoCompleteSelectEvent): void {
    clearTimeout(this.addressBlurRevertHandle);
    this.resolveSelectedAddress(event);
  }

  onZipSelect(event: AutoCompleteSelectEvent): void {
    this.resolveSelectedAddress(event, () => {
      setTimeout(() => document.getElementById('streetAddress')?.focus());
    });
  }

  private resolveSelectedAddress(event: AutoCompleteSelectEvent, onResolved?: () => void): void {
    const selected = event.value as AddressSuggestion;
    if (!selected?.placeId) return;

    this.isResolvingAddress.set(true);
    this.addressLookupError.set(null);
    this.geocodingService.getAddressDetails(selected.placeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.isResolvingAddress.set(false);
        if (!result) {
          this.addressLookupError.set('Could not load details for that address. Please fill in the remaining fields manually.');
          return;
        }
        this.applyLookupResult(result);
        // Exact point of the picked street address (null for a ZIP/region pick, and
        // so also clears any stale GPS reading from an earlier Current Location use).
        this.form().patchValue({ latitude: result.latitude ?? null, longitude: result.longitude ?? null });
        onResolved?.();
      });
  }

  useCurrentLocation(): void {
    if (!('geolocation' in navigator)) {
      this.locationError.set('Location services are not available in this browser.');
      return;
    }

    this.isLocating.set(true);
    this.locationError.set(null);
    this.zipMismatchError.set(null);

    const form = this.form();
    // Captured before the lookup resolves — a ZIP already typed in should describe
    // the same property Current Location is about to resolve.
    const typedZip = this.sanitizeZip(form.get('zipCode')?.value);

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords;
        this.geocodingService.reverseGeocode(latitude, longitude)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(result => {
            this.isLocating.set(false);
            if (!result) {
              this.locationError.set('Could not resolve an address for your current location.');
              return;
            }
            // Don't silently overwrite an already-typed ZIP with a conflicting one —
            // surface it and let the user reconcile instead of guessing which is right.
            if (/^\d{5}$/.test(typedZip) && result.zipCode && result.zipCode !== typedZip) {
              this.zipMismatchError.set(
                `Your current location resolved to ZIP ${result.zipCode}, which doesn't match the ZIP you entered (${typedZip}). Please double-check and try again.`,
              );
              return;
            }
            this.applyLookupResult(result);
            // Raw device GPS — kept alongside the quick client-side address resolve
            // above (which only drives this step's own validation/UX) and sent to
            // the backend, which reverse-geocodes it server-side as the canonical
            // source of truth for the stored address, asynchronously after intake.
            form.patchValue({ latitude, longitude });
          });
      },
      () => {
        this.isLocating.set(false);
        this.locationError.set('Location permission was denied. Please enter your address manually.');
      },
    );
  }

  private triggerZipLookup(zipCode: string | null | undefined): void {
    if (!zipCode || !/^\d{5}$/.test(zipCode) || zipCode === this.lastLookedUpZip) {
      return;
    }
    this.lastLookedUpZip = zipCode;
    this.isLookingUpZip.set(true);
    this.zipLookupError.set(null);

    this.geocodingService.lookupZipCode(zipCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.isLookingUpZip.set(false);
        // The user may have already edited the ZIP away while this request was in
        // flight — applying a late response for a ZIP that's no longer current would
        // silently resurrect the exact stale-data bug clearResolvedLocationFields
        // exists to prevent.
        if (this.form().get('zipCode')?.value !== zipCode) {
          return;
        }
        if (result) {
          this.applyLookupResult(result, zipCode);
          if (result.bounds) {
            this.prefillFirstStreetAddress(zipCode, result.bounds);
          }
        } else {
          this.zipLookupError.set('Could not find city/state for this ZIP code. Please enter them manually.');
        }
      });
  }

  // A bare ZIP forward-geocode has no street match, so applyLookupResult above
  // leaves Street Address blank. Reverse-geocoding the ZIP's own bounding-box
  // center (reverseGeocode already prefers a street-address-type result) gives a
  // real address inside that ZIP to pre-fill as a default — the homeowner can
  // still pick a different one via the autocomplete. Only touches street/unit:
  // city/state/county/zipCode are already correct from the ZIP lookup above, and
  // a centroid landing just outside the ZIP shouldn't silently overwrite them.
  private prefillFirstStreetAddress(zipCode: string, bounds: google.maps.LatLngBoundsLiteral): void {
    const centerLat = (bounds.north + bounds.south) / 2;
    const centerLng = (bounds.east + bounds.west) / 2;
    this.geocodingService.reverseGeocode(centerLat, centerLng)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        const form = this.form();
        const stillCurrent = form.get('zipCode')?.value === zipCode && !form.get('streetAddress')?.value;
        if (!result?.streetAddress || !stillCurrent || result.zipCode !== zipCode) {
          return;
        }
        form.patchValue({ streetAddress: result.streetAddress, unit: result.unit || form.get('unit')?.value });
        this.resolvedBounds.set(result.bounds ?? bounds);
      });
  }

  // Shared by ZIP-triggered lookups (city/county/state only) and the
  // "Current Location" shortcut (also street/unit).
  private applyLookupResult(result: ZipLookupResult, fallbackZip?: string): void {
    const form = this.form();
    form.patchValue({
      city: result.city || form.get('city')?.value,
      state: resolveStateOption(result.state),
      county: resolveCountyOption(result.county) || form.get('county')?.value,
      zipCode: result.zipCode || fallbackZip || form.get('zipCode')?.value,
      streetAddress: result.streetAddress || form.get('streetAddress')?.value,
      unit: result.unit || form.get('unit')?.value,
    });
    this.resolvedBounds.set(result.bounds);
  }

  // Resets every field that's only ever meant to reflect the currently-entered ZIP.
  // Street Address/Unit/lat-lng are included now — since all are populated only by
  // Current Location (never typed by hand), there's no user input at risk of being
  // clobbered here, and a stale GPS reading shouldn't survive the user editing the ZIP.
  private clearResolvedLocationFields(): void {
    if (!this.lastLookedUpZip) {
      return;
    }
    this.lastLookedUpZip = '';
    this.zipLookupError.set(null);
    this.zipMismatchError.set(null);
    this.addressQuery = '';
    this.addressSuggestions.set([]);
    this.resolvedBounds.set(null);
    this.form().patchValue(
      { city: '', county: '', state: 'CA', streetAddress: '', unit: '', latitude: null, longitude: null },
      { emitEvent: false },
    );
  }

  isInvalid(controlName: string): boolean {
    const control = this.form().get(controlName);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  errorFor(controlName: string, messages: Record<string, string>): string | null {
    return fieldErrorMessage(this.form().get(controlName), messages);
  }
}
